import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import '../../../component-styles/Proposal-UI.css';
import { isInvoicedLineItem } from '../../../utils/proposalInvoicing.js';

function formatCurrency(v) {
  return Number(v || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function itemLabel(item) {
  return item.clientName || item.name || item.description || 'Untitled item';
}

// Lets the designer pick which proposal items go onto a new invoice; the rest
// stay on the proposal to be invoiced later. Items already invoiced are listed
// (so the proposal reads as a whole) but can't be picked again.
export default function ConvertToInvoiceModal({ items, paid = 0, onCancel, onConfirm }) {
  const lineItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const selectableIds = useMemo(
    () => lineItems.filter((it) => !isInvoicedLineItem(it)).map((it) => it.itemId),
    [lineItems]
  );
  // Everything still on the proposal starts selected, so invoicing the whole
  // proposal stays a single click.
  const [selected, setSelected] = useState(() => new Set(selectableIds));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape' && !submitting) onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel, submitting]);

  const allSelected = selectableIds.length > 0 && selected.size === selectableIds.length;

  function toggle(itemId) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }

  async function handleConvert() {
    if (!selected.size || submitting) return;
    setSubmitting(true);
    try {
      // Keep the proposal's own item order on the invoice.
      await onConfirm(selectableIds.filter((id) => selected.has(id)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="proposal-lock-overlay" onClick={() => !submitting && onCancel()}>
      <div
        className="proposal-lock-modal convert-invoice-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="convert-invoice-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="convert-invoice-title">Convert to Invoice</h3>
        <p>
          Select the items you want to convert into an invoice. Unselected items will remain on the
          proposal. A draft invoice will be created, and your client won&apos;t see it until you send it.
        </p>

        <div className="convert-invoice-list-header">
          <span>Select items</span>
          {selectableIds.length > 1 && (
            <button
              type="button"
              className="convert-invoice-select-all"
              onClick={() => setSelected(allSelected ? new Set() : new Set(selectableIds))}
              disabled={submitting}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </button>
          )}
        </div>

        <div className="convert-invoice-list">
          {lineItems.map((it) => {
            const invoiced = isInvoicedLineItem(it);
            const checked = !invoiced && selected.has(it.itemId);
            return (
              <label
                key={it.itemId}
                className={`convert-invoice-row${checked ? ' is-selected' : ''}${invoiced ? ' is-invoiced' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={invoiced || submitting}
                  onChange={() => toggle(it.itemId)}
                />
                <span className="convert-invoice-row-text">
                  <span className="convert-invoice-row-name">{itemLabel(it)}</span>
                  {(it.room || invoiced || it.clientApproved === false) && (
                    <span className="convert-invoice-row-meta">
                      {it.room && <span>{it.room}</span>}
                      {invoiced && (
                        <span className="convert-invoice-tag">
                          Invoiced{it.convertedInvoiceNumber ? ` · ${it.convertedInvoiceNumber}` : ''}
                        </span>
                      )}
                      {!invoiced && it.clientApproved === false && (
                        <span className="convert-invoice-tag is-declined">Declined by client</span>
                      )}
                    </span>
                  )}
                </span>
                <span className="convert-invoice-row-amount">{formatCurrency(it.lineTotal)}</span>
              </label>
            );
          })}
        </div>

        <div className="convert-invoice-count">
          {selected.size} of {selectableIds.length} item{selectableIds.length === 1 ? '' : 's'} selected
        </div>
        {Number(paid) > 0 && (
          <div className="convert-invoice-note">
            The {formatCurrency(paid)} already paid on this proposal will be applied to the new invoice,
            up to its total.
          </div>
        )}

        <div className="proposal-lock-actions">
          <button type="button" className="if-btn-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="if-btn-primary"
            onClick={handleConvert}
            disabled={!selected.size || submitting}
          >
            {submitting ? 'Converting…' : 'Convert to Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}

ConvertToInvoiceModal.propTypes = {
  items: PropTypes.array.isRequired,
  paid: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
